import { useSyncExternalStore } from 'react';
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';

/**
 * O MENU LATERAL RECOLHIDO — uma escolha só, para as três barras (10/10/2026).
 *
 * A barra lateral ocupa 256 px, e em 1366 px de largura isso é quase um quinto da tela. Recolhida
 * ela vira uma faixa de ícones (64 px) e devolve ~190 px ao conteúdo.
 *
 * ─── UMA ESCOLHA, E NÃO UMA POR ÁREA ───────────────────────────────────────────
 *
 * Professor, gestão e administrador têm barras diferentes (`TeacherSidebar`, `GestaoSidebar`,
 * `AdminSidebar`), e quem recolheu numa espera encontrar recolhida na outra: a chave é uma, e a
 * regra mora aqui, consultada pelas três. Três cópias divergiriam na primeira mudança.
 *
 * ─── GUARDADA NO NAVEGADOR ─────────────────────────────────────────────────────
 *
 * É preferência de tela, não dado: não vai ao servidor. Sem `localStorage` (navegação privada
 * restrita) a barra simplesmente abre expandida, e recolher vale até recarregar.
 *
 * ─── SÓ NA BARRA FIXA ──────────────────────────────────────────────────────────
 *
 * A GAVETA do celular nunca recolhe — ela já é temporária, e faixa de ícones dentro de uma gaveta
 * de 260 px não devolve espaço a ninguém. O `if` mora em cada barra: `variant === 'sidebar'`.
 */
const CHAVE = 'menu-lateral-recolhido';
const EVENTO = 'menu-lateral-recolhido:mudou';
let naMemoria = false;

function ler(): boolean {
  try { return localStorage.getItem(CHAVE) === '1'; } catch { return naMemoria; }
}
function assinar(avisar: () => void): () => void {
  window.addEventListener(EVENTO, avisar);
  // Outra aba do mesmo navegador mudou a escolha.
  window.addEventListener('storage', avisar);
  return () => { window.removeEventListener(EVENTO, avisar); window.removeEventListener('storage', avisar); };
}

export function alternarMenuRecolhido(): void {
  const novo = !ler();
  naMemoria = novo;
  try { localStorage.setItem(CHAVE, novo ? '1' : '0'); } catch { /* sem armazenamento: vale até recarregar */ }
  window.dispatchEvent(new Event(EVENTO));
}

export function useMenuRecolhido(): boolean {
  return useSyncExternalStore(assinar, ler, () => false);
}

/** O botão que recolhe e expande. O mesmo nas três barras. */
export function BotaoDeRecolherMenu({ recolhido, className = '' }: { recolhido: boolean; className?: string }) {
  const Icone = recolhido ? ChevronDoubleRightIcon : ChevronDoubleLeftIcon;
  const rotulo = recolhido ? 'Expandir o menu' : 'Recolher o menu';
  return (
    <button
      type="button"
      onClick={alternarMenuRecolhido}
      aria-label={rotulo}
      title={rotulo}
      aria-pressed={recolhido}
      data-testid="recolher-menu"
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 ${className}`}
    >
      <Icone className="h-5 w-5" />
    </button>
  );
}
